import { Injectable } from '@angular/core';

export type KontingentType = 'kueche' | 'moebel' | 'other';

export interface DispatchCsvRow {
  filiale: string;
  kaufvertragsnummer: string;
  kaufvertragsposition: string;
  artikelnummer: string;
  nachname: string;
  vorname: string;
  strasse: string;
  plz: string;
  ort: string;
  termin: string;
  tourBezeichnung: string;
  tourSchluessel: string;
  kontingentRaw: string;
  kontingent: KontingentType;
  kontingentLabel: string;
  artikelbezeichnung: string;
  liefermenge: number;
  lieferkostenEK: number;
  montagemenge: number;
  montagekostenEK: number;
  gewichtKg: number;
  volumenM3: number;
  email: string;
  mobil: string;
  telefon: string;
}

export interface DispatchArticle {
  position: string;
  artikelnummer: string;
  bezeichnung: string;
  liefermenge: number;
  lieferkostenEK: number;
  montagemenge: number;
  montagekostenEK: number;
  gewichtKg: number;
  volumenM3: number;
}

export interface DispatchCsvStation {
  filiale: string;
  kaufvertragsnummer: string;
  address: string;
  plz: string;
  ort: string;
  customerName: string;
  nachname: string;
  termin: string;
  tourBezeichnung: string;
  tourSchluessel: string;
  kontingent: KontingentType;
  kontingentLabel: string;
  positionCount: number;
  artikelCount: number;
  liefermenge: number;
  lieferkostenEK: number;
  montagemenge: number;
  montagekostenEK: number;
  gewichtKg: number;
  volumenM3: number;
  kostenGesamt: number;
  articles: DispatchArticle[];
  email: string;
  mobil: string;
  telefon: string;
}

@Injectable({
  providedIn: 'root'
})
export class DispatchPlannerCsvService {
  /** Erste 2 Ziffern der KV/Auftragsnummer = Filial-Kurzcode (customer_branch.short). */
  extractBranchShortFromOrderNumber(orderNumber: string): string | null {
    const kv = (orderNumber || '')
      .trim()
      .replace(/^\uFEFF/, '')
      .replace(/^kv[\s.:;-]*/i, '');
    if (!kv) {
      return null;
    }
    const match = kv.match(/^(\d{2})/);
    return match ? match[1] : null;
  }

  collectBranchCodesFromOrderNumbers(stations: DispatchCsvStation[], content?: string): string[] {
    return Array.from(this.countBranchCodesFromOrderNumbers(stations, content).keys());
  }

  private countBranchCodesFromOrderNumbers(
    stations: DispatchCsvStation[],
    content?: string
  ): Map<string, number> {
    const counts = new Map<string, number>();
    const add = (orderNumber: string) => {
      const short = this.extractBranchShortFromOrderNumber(orderNumber);
      if (!short) {
        return;
      }
      counts.set(short, (counts.get(short) || 0) + 1);
    };

    stations.forEach(station => add(station.kaufvertragsnummer));
    if (content) {
      this.parseCsv(content).forEach(row => add(row.kaufvertragsnummer));
    }
    return counts;
  }

  private pickDominantBranchCode(counts: Map<string, number>): string | null {
    if (!counts.size) {
      return null;
    }
    const ranked = Array.from(counts.entries()).sort((a, b) => b[1] - a[1]);
    if (ranked.length === 1) {
      return ranked[0][0];
    }
    return ranked[0][1] > ranked[1][1] ? ranked[0][0] : null;
  }

  private collectUniqueEmails(stations: DispatchCsvStation[], content?: string): string[] {
    const emails = new Set<string>();
    const add = (value: string) => {
      const email = (value || '').trim().replace(/^mailto:/i, '');
      if (email.includes('@')) {
        emails.add(email);
      }
    };

    stations.forEach(station => add(station.email));
    if (content) {
      this.parseCsv(content).forEach(row => add(row.email));
    }
    return Array.from(emails);
  }

  collectFilialeCodes(content: string): string[] {
    const rows = this.parseCsv(content);
    return Array.from(
      new Set(rows.map(row => (row.filiale || '').trim()).filter(Boolean))
    );
  }

  collectBranchHints(stations: DispatchCsvStation[], content?: string): string[] {
    const kvCounts = this.countBranchCodesFromOrderNumbers(stations, content);
    const dominantKv = this.pickDominantBranchCode(kvCounts);
    if (dominantKv) {
      return [dominantKv];
    }
    if (kvCounts.size > 1) {
      return Array.from(kvCounts.keys());
    }

    const emails = this.collectUniqueEmails(stations, content);
    if (emails.length === 1) {
      return emails;
    }

    const hints = new Set<string>();
    stations.forEach(station => {
      const filiale = (station.filiale || '').trim();
      if (filiale) {
        hints.add(filiale);
      }
    });
    if (!hints.size && content) {
      this.collectFilialeCodes(content).forEach(code => hints.add(code));
    }
    return Array.from(hints);
  }

  /** Eindeutiger Filial-Hinweis aus importierten Aufträgen (KV-Präfix, E-Mail, Filiale-Spalte). */
  resolveSingleBranchHint(
    orderNumbers: string[],
    filialeValues: string[] = [],
    emails: string[] = []
  ): string | null {
    const kvCounts = new Map<string, number>();
    orderNumbers.forEach(orderNumber => {
      const short = this.extractBranchShortFromOrderNumber(orderNumber);
      if (!short) {
        return;
      }
      kvCounts.set(short, (kvCounts.get(short) || 0) + 1);
    });
    const dominantKv = this.pickDominantBranchCode(kvCounts);
    if (dominantKv) {
      return dominantKv;
    }

    const uniqueEmails = Array.from(
      new Set(
        emails
          .map(email => (email || '').trim().replace(/^mailto:/i, ''))
          .filter(email => email.includes('@'))
      )
    );
    if (uniqueEmails.length === 1) {
      return uniqueEmails[0];
    }

    const uniqueFiliale = Array.from(
      new Set(filialeValues.map(value => (value || '').trim()).filter(Boolean))
    );
    return uniqueFiliale.length === 1 ? uniqueFiliale[0] : null;
  }

  parseAndGroup(content: string): DispatchCsvStation[] {
    const rows = this.parseCsv(content);
    if (!rows.length) {
      return [];
    }

    return this.groupCsvRows(rows);
  }

  groupSoapTourRows(soapRows: Record<string, string>[]): DispatchCsvStation[] {
    const rows = soapRows
      .map((row) => this.soapTourRowToCsvRow(row))
      .filter((row) => row.kaufvertragsnummer.trim());
    if (!rows.length) {
      return [];
    }

    return this.groupCsvRows(rows);
  }

  private groupCsvRows(rows: DispatchCsvRow[]): DispatchCsvStation[] {
    const grouped = new Map<string, DispatchCsvRow[]>();
    rows.forEach((row) => {
      const kv = row.kaufvertragsnummer.trim();
      if (!kv) {
        return;
      }
      if (!grouped.has(kv)) {
        grouped.set(kv, []);
      }
      grouped.get(kv)!.push(row);
    });

    return Array.from(grouped.entries()).map(([kv, kvRows]) => this.buildStation(kv, kvRows));
  }

  private soapTourRowToCsvRow(row: Record<string, string>): DispatchCsvRow {
    const pick = (key: string) => (row[key] || '').trim();
    const kontingent = this.mapKontingent(pick('Kontingent'));
    const telefon = pick('Telefon1') || pick('Telefon2');

    return {
      filiale: pick('Filiale'),
      kaufvertragsnummer: pick('Kaufvertragsnummer'),
      kaufvertragsposition: pick('Kaufvertragsposition'),
      artikelnummer: pick('Artikelnummer'),
      nachname: pick('Nachname'),
      vorname: pick('Vorname'),
      strasse: pick('Strasse'),
      plz: pick('PLZ'),
      ort: pick('Ort'),
      termin: pick('Termin'),
      tourBezeichnung: pick('TourBezeichnung'),
      tourSchluessel: pick('TourSchluessel'),
      kontingentRaw: pick('Kontingent'),
      kontingent: kontingent.type,
      kontingentLabel: kontingent.label,
      artikelbezeichnung: pick('Artikelbezeichnung'),
      liefermenge: this.parseSoapNumber(pick('Liefermenge')),
      lieferkostenEK: this.parseSoapNumber(pick('LieferkostenEK')),
      montagemenge: this.parseSoapNumber(pick('Montagemenge')),
      montagekostenEK: this.parseSoapNumber(pick('MontagekostenEK')),
      gewichtKg: 0,
      volumenM3: 0,
      email: pick('Email'),
      mobil: pick('Mobil'),
      telefon,
    };
  }

  private parseSoapNumber(value: string): number {
    if (!value) {
      return 0;
    }

    const parsed = Number(value.replace(',', '.'));
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private buildStation(kaufvertragsnummer: string, rows: DispatchCsvRow[]): DispatchCsvStation {
    const primary = rows[0];
    const kontingent = this.resolveGroupKontingent(rows);
    const positions = new Set(rows.map(r => r.kaufvertragsposition).filter(Boolean));
    const artikel = new Set(rows.map(r => r.artikelbezeichnung).filter(Boolean));

    let liefermenge = 0;
    let lieferkostenEK = 0;
    let montagemenge = 0;
    let montagekostenEK = 0;
    let gewichtKg = 0;
    let volumenM3 = 0;

    const articles: DispatchArticle[] = rows.map(row => {
      liefermenge += row.liefermenge;
      lieferkostenEK += row.lieferkostenEK;
      montagemenge += row.montagemenge;
      montagekostenEK += row.montagekostenEK;
      gewichtKg += row.gewichtKg;
      volumenM3 += row.volumenM3;
      return {
        position: row.kaufvertragsposition,
        artikelnummer: row.artikelnummer,
        bezeichnung: row.artikelbezeichnung,
        liefermenge: row.liefermenge,
        lieferkostenEK: row.lieferkostenEK,
        montagemenge: row.montagemenge,
        montagekostenEK: row.montagekostenEK,
        gewichtKg: row.gewichtKg,
        volumenM3: row.volumenM3
      };
    });

    const addressParts = this.resolveAddressParts(primary);

    return {
      filiale:
        (primary.filiale || '').trim() ||
        this.extractBranchShortFromOrderNumber(kaufvertragsnummer) ||
        '',
      kaufvertragsnummer,
      address: addressParts.address,
      plz: addressParts.plz,
      ort: addressParts.ort,
      customerName: `${primary.vorname} ${primary.nachname}`.trim(),
      nachname: (primary.nachname || '').trim(),
      termin: primary.termin,
      tourBezeichnung: primary.tourBezeichnung,
      tourSchluessel: (primary.tourSchluessel || '').trim(),
      kontingent: kontingent.type,
      kontingentLabel: kontingent.label,
      positionCount: positions.size || rows.length,
      artikelCount: artikel.size || rows.length,
      liefermenge,
      lieferkostenEK,
      montagemenge,
      montagekostenEK,
      gewichtKg,
      volumenM3,
      kostenGesamt: lieferkostenEK + montagekostenEK,
      articles,
      email: primary.email,
      mobil: primary.mobil,
      telefon: primary.telefon
    };
  }

  private resolveGroupKontingent(rows: DispatchCsvRow[]): { type: KontingentType; label: string } {
    const counts: Record<KontingentType, number> = { kueche: 0, moebel: 0, other: 0 };
    rows.forEach(row => counts[row.kontingent]++);

    if (counts.kueche >= counts.moebel && counts.kueche > 0) {
      return { type: 'kueche', label: 'Küche' };
    }
    if (counts.moebel > 0) {
      return { type: 'moebel', label: 'Möbel' };
    }
    return { type: 'other', label: rows[0]?.kontingentLabel || 'Sonstiges' };
  }

  buildAddress(row: DispatchCsvRow): string {
    return this.resolveAddressParts(row).address;
  }

  private resolveAddressParts(row: DispatchCsvRow): { address: string; plz: string; ort: string } {
    let strasse = (row.strasse || '').trim();
    let plz = (row.plz || '').trim();
    let ort = (row.ort || '').trim();

    // In Export-CSV sind Straße und Ort teils vertauscht (z. B. Ort = "SUDETENWEG 6", Strasse = "KORNTAL-MUENCHINGEN")
    if (/^\d{5}$/.test(plz) && this.looksLikeStreet(ort) && !this.looksLikeStreet(strasse)) {
      const tmp = strasse;
      strasse = ort;
      ort = tmp;
    }

    let address = '';
    if (strasse && plz && ort) {
      address = `${strasse}, ${plz} ${ort}`;
    } else if (plz && ort) {
      address = `${plz} ${ort}`;
    } else {
      address = [strasse, plz, ort].filter(Boolean).join(', ');
    }

    return { address, plz, ort };
  }

  private looksLikeStreet(value: string): boolean {
    return /\d/.test(value);
  }

  private parseCsv(content: string): DispatchCsvRow[] {
    const lines = content.split(/\r?\n/).filter(line => line.trim());
    if (lines.length < 2) {
      return [];
    }

    const delimiter = this.detectDelimiter(lines[0]);
    const headers = this.parseCsvLine(lines[0], delimiter);
    const headerMap = this.buildHeaderMap(headers);

    return lines
      .slice(1)
      .map(line => this.mapRow(this.parseCsvLine(line, delimiter), headerMap))
      .filter(row => !!row.kaufvertragsnummer);
  }

  private detectDelimiter(headerLine: string): string {
    const counts = [
      { delimiter: ';', count: (headerLine.match(/;/g) || []).length },
      { delimiter: ',', count: (headerLine.match(/,/g) || []).length },
      { delimiter: '\t', count: (headerLine.match(/\t/g) || []).length }
    ];
    counts.sort((a, b) => b.count - a.count);
    return counts[0]?.count > 0 ? counts[0].delimiter : ';';
  }

  private buildHeaderMap(headers: string[]): Record<string, number> {
    const map: Record<string, number> = {};
    headers.forEach((header, index) => {
      map[this.normalizeHeader(header)] = index;
    });
    return map;
  }

  private normalizeHeader(header: string): string {
    return header
      .replace(/^\uFEFF/, '')
      .replace(/^["']|["']$/g, '')
      .trim()
      .toLowerCase()
      .replace(/ä/g, 'a')
      .replace(/ö/g, 'o')
      .replace(/ü/g, 'u')
      .replace(/ß/g, 'ss');
  }

  private mapRow(values: string[], headerMap: Record<string, number>): DispatchCsvRow {
    const get = (...names: string[]) => this.getColumn(values, headerMap, names);
    const kontingentRaw = get('Kontingent');
    const kontingent = this.mapKontingent(kontingentRaw);

    return {
      filiale: this.readFiliale(values, headerMap, get),
      kaufvertragsnummer: get('Kaufvertragsnummer', 'KV-Nummer', 'Kaufvertrag'),
      kaufvertragsposition: get('Kaufvertragsposition', 'Position'),
      artikelnummer: get('Artikelnummer', 'Artikel-Nr'),
      nachname: get('Nachname'),
      vorname: get('Vorname'),
      strasse: get('Strasse', 'Straße', 'Strasse'),
      plz: get('PLZ', 'Postleitzahl'),
      ort: get('Ort', 'Stadt'),
      termin: get('Termin'),
      tourBezeichnung: get('TourBezeichnung', 'Tour'),
      tourSchluessel: get('TourSchluessel', 'Tour-Schlüssel', 'Tour Schluessel'),
      kontingentRaw,
      kontingent: kontingent.type,
      kontingentLabel: kontingent.label,
      artikelbezeichnung: get('Artikelbezeichnung', 'Artikel'),
      liefermenge: this.parseDecimal(get('Liefermenge')),
      lieferkostenEK: this.parseLieferkostenEk(get),
      montagemenge: this.parseDecimal(get('Montagemenge', 'Montageeinheiten', 'Montage-Einheiten')),
      montagekostenEK: this.parseMontagekostenEk(get),
      gewichtKg: this.parseDecimal(get('Gewicht', 'GewichtKg', 'Gewicht kg', 'Gewicht (kg)')),
      volumenM3: this.parseDecimal(get('Volumen', 'VolumenM3', 'Volumen m3', 'Volumen (m3)', 'Volumen (m³)')),
      email: get('Email', 'E-Mail'),
      mobil: get('Mobil', 'Handy'),
      telefon: get('Telefon1', 'Telefon2', 'Telefon')
    };
  }

  private parseLieferkostenEk(get: (...names: string[]) => string): number {
    const ek = this.parseDecimal(
      get('LieferkostenEK', 'LieferEK', 'Lieferkosten EK', 'Liefer EK')
    );
    if (ek) {
      return ek;
    }
    return this.parseDecimal(get('LieferkostenVK', 'Lieferkosten'));
  }

  private parseMontagekostenEk(get: (...names: string[]) => string): number {
    const ek = this.parseDecimal(
      get('MontagekostenEK', 'MontageEK', 'Montagekosten EK', 'Montage EK')
    );
    if (ek) {
      return ek;
    }
    return this.parseDecimal(get('MontagekostenVK', 'Montagekosten'));
  }

  private parseDecimal(raw: string): number {
    const s = (raw || '').trim().replace(/\s/g, '');
    if (!s) {
      return 0;
    }
    if (/,\d{1,2}$/.test(s) && s.includes('.')) {
      return parseFloat(s.replace(/\./g, '').replace(',', '.')) || 0;
    }
    if (s.includes(',') && !s.includes('.')) {
      return parseFloat(s.replace(',', '.')) || 0;
    }
    return parseFloat(s.replace(',', '.')) || 0;
  }

  private readFiliale(
    values: string[],
    headerMap: Record<string, number>,
    get: (...names: string[]) => string
  ): string {
    const explicit = get(
      'Filiale',
      'Wareneingangsfiliale',
      'Filial-Nr',
      'Filialnummer',
      'Filialnr',
      'Filial-Nr.',
      'Auslieferungsfiliale',
      'Beliefersfiliale',
      'WE-Filiale',
      'WE Filiale',
      'customer_branch_id',
      'CustomerBranchId',
      'Filialbezeichnung',
      'Filialname',
      'Niederlassung',
      'Filialmail',
      'Filiale Mail',
      'E-Mail Filiale',
      'Email Filiale',
      'Filiale Email',
      'Filial E-Mail'
    );
    if (explicit) {
      return explicit;
    }

    for (const [header, index] of Object.entries(headerMap)) {
      const isBranchHeader =
        header.includes('filial') ||
        header.includes('wareneingang') ||
        header.includes('niederlassung') ||
        (header.includes('mail') && header.includes('filial'));
      if (!isBranchHeader) {
        continue;
      }
      const value = values[index]?.trim();
      if (value) {
        return value;
      }
    }

    return '';
  }

  private getColumn(values: string[], headerMap: Record<string, number>, names: string[]): string {
    for (const name of names) {
      const idx = headerMap[this.normalizeHeader(name)];
      if (idx !== undefined) {
        const value = values[idx]?.trim();
        if (value) {
          return value;
        }
      }
    }

    for (const name of names) {
      const normalized = this.normalizeHeader(name);
      const matchedKey = Object.keys(headerMap).find(
        key => key.includes(normalized) || normalized.includes(key)
      );
      if (matchedKey !== undefined) {
        const value = values[headerMap[matchedKey]]?.trim();
        if (value) {
          return value;
        }
      }
    }

    return '';
  }

  private mapKontingent(raw: string): { type: KontingentType; label: string } {
    const value = this.normalizeForMatch(raw);
    if (value.includes('kuch') || value.includes('kuech') || value.includes('kche')) {
      return { type: 'kueche', label: 'Küche' };
    }
    if (value.includes('mobel') || value.includes('moebel') || value.includes('mbel')) {
      return { type: 'moebel', label: 'Möbel' };
    }
    if (raw.trim()) {
      return { type: 'other', label: raw.trim() };
    }
    return { type: 'other', label: 'Sonstiges' };
  }

  /** Umlaute vereinheitlichen für robustes Matching (auch bei fehlerhafter Kodierung). */
  private normalizeForMatch(value: string): string {
    return value
      .trim()
      .toLowerCase()
      .replace(/\uFFFD/g, '')
      .replace(/ä/g, 'a')
      .replace(/ö/g, 'o')
      .replace(/ü/g, 'u')
      .replace(/ß/g, 'ss');
  }

  private parseCsvLine(line: string, delimiter = ';'): string[] {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
        continue;
      }
      if (char === delimiter && !inQuotes) {
        result.push(current);
        current = '';
        continue;
      }
      current += char;
    }
    result.push(current);
    return result.map(v => v.trim());
  }
}
